import React from 'react';
import type { PurchaseApprovalStep } from '../../types/purchasing';
import PurchaseLinkedText from './PurchaseLinkedText';
import './ApprovalMap.css';

type TrailState = 'done' | 'current' | 'todo' | 'returned' | 'rejected' | 'cancelled';

export type ApprovalTrailOrigin = {
  userName: string;
  at?: Date;
  /** Defaults to Requested. Already-purchased rows use Recorded. */
  roleLabel?: string;
  /** Verb before the date. Defaults to Submitted. */
  statusVerb?: string;
};

export type ApprovalTrailFollowOn = {
  roleLabel?: string;
  userName?: string;
  at?: Date;
  note?: string;
  done?: boolean;
  /** Replaces the default "Purchased" status when the buy was already made. */
  doneStatus?: string;
};

type TrailItem = {
  key: string;
  roleLabel: string;
  userName?: string;
  state: TrailState;
  status: string;
  note?: string;
};

const ApprovalMap: React.FC<{
  steps: PurchaseApprovalStep[];
  origin?: ApprovalTrailOrigin;
  followOn?: ApprovalTrailFollowOn;
  bandLabel?: string;
  emptyMessage?: string;
  overBudget?: boolean;
  canOverride?: boolean;
  /** Requester may file the requisition anyway. Issuing the PO stays with leadership. */
  allowSubmitOverBudget?: boolean;
}> = ({ steps, origin, followOn, emptyMessage, overBudget, canOverride, allowSubmitOverBudget }) => {
  const flag = overBudget ? (
    <div className={`po-map-over${canOverride || allowSubmitOverBudget ? ' po-map-over--override' : ''}`} role="status">
      {canOverride
        ? 'Over remaining budget — leadership can still submit or issue.'
        : allowSubmitOverBudget
          ? 'Over remaining budget. You can still submit this requisition for approval.'
          : 'Over remaining budget — reduce the amount or pick another bucket.'}
    </div>
  ) : null;

  const items = buildTrailItems(steps, origin, followOn);
  if (items.length === 0) {
    return (
      <>
        {flag}
        <p className="po-map-empty">{emptyMessage || 'Approval path will appear once amount and cost object are set.'}</p>
      </>
    );
  }

  const currentIdx = items.findIndex(item => item.state === 'current' || item.state === 'returned' || item.state === 'rejected');
  const currentItem = currentIdx >= 0 ? items[currentIdx] : undefined;
  const crumbLabel =
    currentIdx >= 0 && currentItem
      ? `Stage ${currentIdx + 1} of ${items.length}: waiting on ${currentItem.userName || currentItem.roleLabel}`
      : `All ${items.length} stages complete`;

  return (
    <div className="po-map">
      {flag}
      <ol className="po-map-stages" aria-label={crumbLabel}>
        {items.map((item, idx) => {
          const now = item.state === 'current' || item.state === 'returned' || item.state === 'rejected';
          const nextState = items[idx + 1]?.state;
          return (
            <li key={item.key} className={`po-map-stage po-map-stage--${item.state}`}>
              <div className="po-map-stage-rail" aria-hidden>
                <span className={`po-map-dot po-map-dot--${item.state}`} />
                {idx < items.length - 1 && (
                  <span
                    className={`po-map-line${
                      item.state === 'cancelled'
                        ? ' po-map-line--cancelled'
                        : nextState && nextState !== 'todo'
                          ? ' po-map-line--done'
                          : ''
                    }`}
                  />
                )}
              </div>
              <div className="po-map-stage-copy">
                <div className="po-map-stage-head">
                  <span className="po-map-stage-title">{item.roleLabel}</span>
                  {now && <span className="po-map-stage-now">now</span>}
                </div>
                {item.userName && <div className="po-map-stage-name">{item.userName}</div>}
                <div className="po-map-stage-status">{item.status}</div>
                {item.note?.trim() && (
                  <div className="po-map-stage-note">
                    <PurchaseLinkedText text={item.note.trim()} />
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

/** Cancelled PRs — pending steps and legacy rows still marked approved in Firestore. */
export function approvalStepsForPurchaseDisplay(
  steps: PurchaseApprovalStep[],
  row: { status: string; cancelledByName?: string; cancelledAt?: Date },
): PurchaseApprovalStep[] {
  if (row.status !== 'cancelled') return steps;
  if (steps.length === 0) return steps;

  const note = row.cancelledByName ? `Cancelled by ${row.cancelledByName}` : 'Cancelled';
  const decidedAt =
    row.cancelledAt instanceof Date
      ? row.cancelledAt
      : row.cancelledAt
        ? new Date(row.cancelledAt)
        : undefined;

  let mapped = steps.map((step) =>
    step.status === 'pending'
      ? { ...step, status: 'skipped' as const, note, decidedAt: decidedAt || step.decidedAt }
      : step,
  );

  if (!mapped.some(isCancellationSkip) && mapped.length > 0) {
    const lastIdx = mapped.length - 1;
    const last = mapped[lastIdx];
    mapped = mapped.map((step, idx) =>
      idx === lastIdx
        ? { ...last, status: 'skipped' as const, note, decidedAt: decidedAt || last.decidedAt }
        : step,
    );
  }

  return mapped;
}

export function buildTrailItems(
  steps: PurchaseApprovalStep[],
  origin?: ApprovalTrailOrigin,
  followOn?: ApprovalTrailFollowOn,
): TrailItem[] {
  const items: TrailItem[] = [];
  if (origin?.userName) {
    const when = formatStepWhen(origin.at);
    const verb = origin.statusVerb || 'Submitted';
    items.push({
      key: 'origin',
      roleLabel: origin.roleLabel || 'Requested',
      userName: origin.userName,
      state: origin.at ? 'done' : 'todo',
      status: when ? `${verb} ${when}` : verb === 'Recorded' ? 'Recorded' : 'Will submit',
    });
  }

  const stepStates = trailStates(steps);
  steps.forEach((step, idx) => {
    const state = stepStates[idx] || 'todo';
    const cancelled = isCancellationSkip(step);
    items.push({
      key: `${step.gate}-${step.userId}-${idx}`,
      roleLabel: step.roleLabel,
      userName: step.userName,
      state,
      status: statusLine(step, state),
      note: cancelled ? undefined : step.note,
    });
  });

  if (followOn) {
    const blocked = items.some(
      item => item.state === 'returned' || item.state === 'rejected' || item.state === 'cancelled',
    );
    const waitingOnApproval = steps.some(step => step.status === 'pending');
    const state: TrailState = followOn.done
      ? 'done'
      : blocked
        ? 'todo'
        : waitingOnApproval
          ? 'todo'
          : 'current';
    const when = formatStepWhen(followOn.at);
    items.push({
      key: 'follow-on',
      roleLabel: followOn.roleLabel || 'Purchase',
      userName: followOn.userName,
      state,
      status: followOn.done
        ? when
          ? `${followOn.doneStatus || 'Purchased'} ${when}`
          : followOn.doneStatus || 'Purchased'
        : state === 'current'
          ? followOn.note?.trim() || 'Waiting'
          : 'Next',
    });
  }

  return items;
}

function isCancellationSkip(step: PurchaseApprovalStep): boolean {
  if (step.status !== 'skipped') return false;
  const note = (step.note || '').trim().toLowerCase();
  return note === 'cancelled' || note.startsWith('cancelled by');
}

function trailStates(steps: PurchaseApprovalStep[]): TrailState[] {
  const blockedIdx = steps.findIndex(step => step.status === 'returned' || step.status === 'rejected');
  const pendingIdx = steps.findIndex(step => step.status === 'pending');
  return steps.map((step, idx) => {
    if (isCancellationSkip(step)) return 'cancelled';
    if (step.status === 'approved' || step.status === 'skipped') return 'done';
    if (step.status === 'returned') return 'returned';
    if (step.status === 'rejected') return 'rejected';
    if (blockedIdx >= 0) return 'todo';
    if (idx === pendingIdx) return 'current';
    return 'todo';
  });
}

function formatStepWhen(value: Date | undefined): string {
  if (!value) return '';
  const at = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function statusLine(step: PurchaseApprovalStep, state: TrailState): string {
  const when = formatStepWhen(step.decidedAt);
  if (isCancellationSkip(step)) {
    const by = (step.note || '').trim().match(/^Cancelled by (.+)$/i);
    if (by?.[1]) return `Cancelled by ${by[1]}`;
    return when ? `Cancelled ${when}` : 'Cancelled';
  }
  if (step.status === 'approved') return when ? `Approved ${when}` : 'Approved';
  if (step.status === 'skipped') return when ? `Skipped ${when}` : 'Skipped';
  if (step.status === 'returned') return when ? `Returned ${when}` : 'Returned';
  if (step.status === 'rejected') return when ? `Rejected ${when}` : 'Rejected';
  return state === 'current' ? 'Waiting' : 'Next';
}

export default ApprovalMap;
